// src/components/Header.tsx
import { useState, useEffect, useRef } from 'react';
import { Menu, X, Plus, User, Settings, LogOut, FileText, ChevronRight } from 'lucide-react'; // Cleaned unused imports
import { Link, useRouter } from './Router';
import { useAuth } from '../contexts/AuthContext';

const NAVIGATION_LINKS = [
  { name: 'ANNONCES', href: '/annonces' },
  { name: 'ARTICLES', href: '/actualite' },
  { name: 'ESTIMATION', href: '/estimateur' },
  { name: 'PRIX DU NEUF', href: '/neuf' },
  { name: 'CONTACT', href: '/contact' },
];

export function Header() {
  const { user, signOut } = useAuth();
  const { navigate } = useRouter();
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- UPDATED NAVIGATION HANDLER ---
  // This function simulates the "Refresh" feel by instantly resetting the view
  const handleNavigation = () => {
    // 1. Close all menus
    setMobileMenuOpen(false);
    setProfileDropdownOpen(false);
    
    // 2. Instant scroll to top (This creates the "New Page" feeling)
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // Use 'auto' for instant snap, 'smooth' for gliding
    });
  };

  const handleLogout = async () => {
    await signOut();
    setProfileDropdownOpen(false);
    navigate('/'); 
  };

  const handleSubmitListingClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleNavigation(); // Use the new handler here too
    navigate('/soumettre-annonce');
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out border-b ${
          scrolled 
            ? 'h-[72px] bg-white/85 backdrop-blur-xl border-gray-200/60 shadow-[0_2px_20px_rgba(0,0,0,0.04)]' 
            : 'h-[80px] bg-white/60 backdrop-blur-md border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full">
            
            {/* 1. Logo */}
            <Link 
              to="/" 
              className="flex items-center flex-shrink-0" 
              onClick={handleNavigation} // Added handler
            > 
              <img 
                src="https://www.argusautomobile.com/logo.png" 
                alt="ARGUS TUNISIA" 
                className={`w-auto object-contain transition-all duration-300 ${scrolled ? 'h-10' : 'h-12'}`} 
              />
            </Link>

            {/* 2. Desktop Navigation */}
            <nav className="hidden lg:flex items-center justify-center space-x-1">
              {NAVIGATION_LINKS.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={handleNavigation} // Added handler
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100/50 transition-all duration-200 tracking-wide"
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* 3. Actions */}
            <div className="hidden lg:flex items-center space-x-3">
              <button
                onClick={handleSubmitListingClick}
                className="group inline-flex items-center gap-2 bg-[#D5462E] text-white pl-4 pr-5 py-2.5 rounded-full font-medium text-sm hover:bg-[#b83a24] shadow-sm hover:shadow-lg hover:shadow-red-500/20 transition-all duration-300 active:scale-95"
              >
                <Plus className="h-4 w-4" />
                <span>PUBLIER</span>
              </button>

              {user ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 border ${
                      profileDropdownOpen ? 'bg-gray-100 border-gray-300' : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <User className="h-5 w-5 text-gray-600" />
                  </button>

                  {/* Dropdown */}
                  <div 
                    className={`absolute right-0 mt-3 w-60 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 transform transition-all duration-200 origin-top-right ${
                      profileDropdownOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                    }`}
                  >
                    <div className="px-4 py-3 border-b border-gray-50">
                      <p className="text-xs text-gray-500 font-medium">CONNECTÉ EN TANT QUE</p>
                      <p className="text-sm font-bold text-gray-900 truncate">{user.email || 'Utilisateur'}</p>
                    </div>
                    
                    <div className="py-2">
                      <Link
                        to="/mes-annonces"
                        onClick={handleNavigation} // Added handler
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span>MES ANNONCES</span>
                      </Link>
                      <Link
                        to="/paramètres-du-compte"
                        onClick={handleNavigation} // Added handler
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Settings className="h-4 w-4 text-gray-400" />
                        <span>PARAMÈTRES</span>
                      </Link>
                    </div>

                    <div className="border-t border-gray-50 mt-1 pt-1">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>DÉCONNEXION</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <Link 
                  to="/connecter"
                  onClick={handleNavigation} // Added handler
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:text-black hover:bg-gray-50 rounded-full transition-all duration-200"
                >
                  CONNEXION
                </Link>
              )}
            </div>

            {/* 4. Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-full transition-all duration-200"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>
      
      {/* Spacer */}
      <div className="h-[80px]"></div> 

      {/* MOBILE MENU DRAWER */}
      <div className={`lg:hidden fixed inset-0 z-50 transition-all duration-300 ${mobileMenuOpen ? 'visible' : 'invisible'}`}>
        <div 
          className={`absolute inset-0 bg-gray-900/20 backdrop-blur-sm transition-opacity duration-300 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileMenuOpen(false)}
        />
        
        <div className={`absolute right-0 top-0 h-full w-[300px] bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
             <img 
                src="https://www.argusautomobile.com/logo.png" 
                alt="Logo" 
                className="h-8 w-auto opacity-90" 
              />
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-full text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto py-6 px-4 space-y-1">
            {NAVIGATION_LINKS.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={handleNavigation} // Added handler
                className="flex items-center justify-between px-4 py-3 text-base font-medium text-gray-700 hover:text-[#D5462E] hover:bg-red-50/50 rounded-xl transition-all"
              >
                {link.name}
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </Link>
            ))}
          </div>

          <div className="p-6 border-t border-gray-100 bg-gray-50/50">
            <button
              onClick={handleSubmitListingClick}
              className="w-full bg-[#D5462E] text-white py-3.5 rounded-xl font-semibold shadow-lg shadow-red-900/10 hover:bg-[#b83a24] active:scale-[0.98] transition-all mb-4"
            >
              PUBLIER UNE ANNONCE
            </button>
            
            {user && (
               <button
               onClick={handleLogout}
               className="w-full flex justify-center items-center gap-2 text-gray-500 py-3 rounded-xl font-medium hover:bg-gray-100 transition-all"
             >
               <LogOut className="h-4 w-4" />
               DÉCONNEXION
             </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}